import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AppBar, Toolbar, Box, IconButton, Button, Typography, InputBase, Paper, Chip, Menu, MenuItem, ListItemIcon, ListItemText, Avatar, Drawer, Backdrop, Divider, Tooltip } from '@mui/material';
import { Notifications, Search, HelpOutline, PhoneInTalk } from '@mui/icons-material';
import MicNoneIcon from '@mui/icons-material/MicNone';
import MenuIcon from '@mui/icons-material/Menu';
import OverlaySidebar from './OverlaySidebar';
import NotificationDrawer from '../common/NotificationDrawer';
import { fetchUnreadCount } from '../../services/notificationsService';
import { manualUnreadCount } from '../../services/notificationsLocalState';
import { Link, useNavigate } from 'react-router-dom';
import UserProfile from '../auth/UserProfile';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from './Layout';
import GoogleLoginButton from '../auth/GoogleLoginButton';
import GlobalSearchOverlay, { SearchItem } from '../common/GlobalSearchOverlay';
import ScreenShareConsent from '../common/ScreenShareConsent';
import { onScreenshareRequest, sendScreenshareConsent, sendOffer, onAnswer, onIce, sendIce, getSocket, onInternalChatNewMessage, onInternalChatMention, onNotificationsUnreadCount, onSttSessionStarted, onSttSessionStopped, onSttActiveSessions, requestActiveSttSessions, SttActiveSession, onAiJobsChanged } from '../../services/realtimeService';
import { listActiveSttSessions } from '../../services/aiJobsService';
import { getUpcomingEvents, CalendarEvent } from '../../services/calendarService';
import OnlineUsersAvatars from '../common/OnlineUsersAvatars';
import GemmaAvatar from '../common/GemmaAvatar';
import { isInternalChatGemmaSystemUserId } from '../../constants/gemmaIcon';
import { fetchOnlineUsers, sendHeartbeat, OnlineUserDto } from '../../services/presenceService';
import { startCall, startConferenceCall, onIncomingCall } from '../../services/callService';
import { buildCallKey, broadcastCallResolved, onCallResolvedFromOtherTab } from '../../services/callCoordination';
import IncomingCallToast from '../common/IncomingCallToast';
import ConferenceCallDialog from '../common/ConferenceCallDialog';
import GroupsIcon from '@mui/icons-material/Groups';
import CloseIcon from '@mui/icons-material/Close';
import ChatMessageToast from '../common/ChatMessageToast';
import ForumIcon from '@mui/icons-material/Forum';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MoreVertIcon from '@mui/icons-material/MoreVert';
// import ChatPanel from '../common/ChatPanel'; // stary Google Chat
import InternalChatPanel from '../common/InternalChatPanel';
import TasksDrawer from '../common/TasksDrawer';
import GemmaTasksDrawer from '../common/GemmaTasksDrawer';
import { listConversations as listSpaces, listMessages, getOrCreateDirect, markRead as markReadConv } from '../../services/internalChatService';
import { listUsersPublic } from '../../services/userService';
import type { ChatConversation as ChatSpace } from '../../services/internalChatService';
import AIDialog from '../common/AIDialog';
import ChannelDialog from '../chat/ChannelDialog';
import api from '../../services/api';
import { fetchAgentAiStatus } from '../../services/aiAgentStatusService';
import { ReactComponent as TasksSvg } from '../../assets/images/tasks.svg';
import { listMyTasks } from '../../services/localTasksService';
import { fetchGemmaQueueJobs } from '../../services/gemmaQueueApi';

const CHAT_FIELD_WORKERS_GROUP_NAME = 'Wszyscy pracownicy terenowi';
function isFieldWorkersGroupChannelName(name?: string | null) {
  return (name || '').trim().toLowerCase() === CHAT_FIELD_WORKERS_GROUP_NAME.toLowerCase();
}

interface HeaderProps {
  setShortcutsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// Funkcja pomocnicza do formatowania czasu MM:SS
const formatTime = (totalSeconds: number): string => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return `${mm}:${ss}`;
};

const Header: React.FC<HeaderProps> = ({ setShortcutsOpen }) => {
  const { isAuthenticated, isLoading, user, googleLogoutCountdown, idleLogoutCountdown, resetIdleTimer } = useAuth();
  const isFieldWorkerViewer = user?.role === 'field_worker';
  const { showNotification } = useNotification();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSidebarVisible, setIsSidebarVisible] = useState<boolean>(() => {
    try { return (localStorage.getItem('ui:sidebar:visible') ?? '0') === '1'; } catch { return false; }
  });
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadChat, setUnreadChat] = useState<number>(0);
  // lastChatSeenAt nie jest już używany (zastąpiony przez Google Read State)
  const [unread, setUnread] = useState<number>(0);
  const unreadBaseRef = useRef<number>(0); // licznik z backendu bez lokalnych modyfikacji
  const handleUnreadChange = (count: number) => setUnread(Math.max(0, count));
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUserDto[]>([]);
  const [consentOpen, setConsentOpen] = useState(false);
  const [requestFromUserId, setRequestFromUserId] = useState<string | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const [tasksOpen, setTasksOpen] = useState(false);
  const [gemmaTasksOpen, setGemmaTasksOpen] = useState(false);
  /** Aktywne pozycje kolejki Gemmy (kolejka / wstrzymane / w toku) — licznik na ikonie. */
  const [gemmaQueueActiveCount, setGemmaQueueActiveCount] = useState(0);
  const [panelOrder, setPanelOrder] = useState<Array<'notif'|'chat'|'tasks'|'ai'|'gemmaTasks'>>([]);
  // Opóźnienie pokazania overlayu względem panelu: najpierw panel, po chwili overlay (eliminuje migotanie)
  const [overlayReady, setOverlayReady] = useState(false);
  // Najbliższe spotkanie do startu w ciągu 5 minut
  const [nextMeeting, setNextMeeting] = useState<CalendarEvent | null>(null);
  const [currentMeeting, setCurrentMeeting] = useState<CalendarEvent | null>(null);
  const currentEndMsRef = React.useRef<number | null>(null);
  const [meetCountdown, setMeetCountdown] = useState<number>(0);
  // STT recording indicator (blink in topbar)
  const [sttRecording, setSttRecording] = useState<boolean>(() => {
    try { return (localStorage.getItem('stt:recording') || '0') === '1'; } catch { return false; }
  });
  /**
   * Aktywne sesje transkrypcji u innych uczestników — sygnalizacja w topbar,
   * żeby każdy uczestnik spotkania wiedział, że koleżanka/kolega już nagrywa.
   */
  const [remoteSttSessions, setRemoteSttSessions] = useState<SttActiveSession[]>([]);
  useEffect(() => {
    if (!isAuthenticated) { setRemoteSttSessions([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const list = await listActiveSttSessions();
        if (!cancelled) {
          setRemoteSttSessions(list.map((s) => ({
            meetingGoogleId: s.meetingGoogleId,
            meetingId: s.meetingId,
            meetingTitle: s.meetingTitle,
            startedBy: {
              userId: s.startedBy?.userId || '',
              displayName: s.startedBy?.displayName || null,
              avatarUrl: s.startedBy?.avatarUrl || null,
            },
            startedAt: s.startedAt,
            participantUserIds: Array.isArray(s.participantUserIds) ? s.participantUserIds : [],
            jobId: s.jobId,
          })));
        }
      } catch {}
    })();
    try { requestActiveSttSessions(); } catch {}
    const offActive = onSttActiveSessions((sessions) => {
      setRemoteSttSessions(sessions);
    });
    const offStarted = onSttSessionStarted((s) => {
      setRemoteSttSessions((prev) => {
        const uid = String(user?.user_id || '');
        const visible = uid && (s.startedBy.userId === uid || (s.participantUserIds || []).includes(uid));
        if (!visible) return prev;
        const others = prev.filter((x) => x.meetingGoogleId !== s.meetingGoogleId);
        return [s, ...others];
      });
    });
    const offStopped = onSttSessionStopped((gid) => {
      setRemoteSttSessions((prev) => prev.filter((x) => x.meetingGoogleId !== gid));
    });
    return () => {
      cancelled = true;
      try { offActive(); } catch {}
      try { offStarted(); } catch {}
      try { offStopped(); } catch {}
    };
  }, [isAuthenticated, user?.user_id]);
  /** Odśwież licznik zadań Gemmy po zmianach w kolejce ai_jobs. */
  useEffect(() => {
    if (!isAuthenticated) return;
    const off = onAiJobsChanged(() => {
      try { window.dispatchEvent(new CustomEvent('gemma-queue-changed')); } catch {}
    });
    return () => { try { off(); } catch {} };
  }, [isAuthenticated]);
  useEffect(() => {
    const recompute = () => {
      try {
        const rec = (localStorage.getItem('stt:recording') || '0') === '1';
        const atRaw = localStorage.getItem('stt:recording_at') || '0';
        const at = parseInt(atRaw, 10) || 0;
        const fresh = at > 0 ? (Date.now() - at) < 5000 : false;
        setSttRecording(rec && fresh);
      } catch { setSttRecording(false); }
    };
    const onRecording = (e: any) => {
      try {
        const active = !!(e?.detail?.active);
        if (active) { try { localStorage.setItem('stt:recording_at', String(Date.now())); } catch {} }
        setSttRecording(active);
      } catch {}
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'stt:recording') {
        recompute();
      }
      if (e.key === 'stt:recording_at') recompute();
    };
    const onVisibility = () => recompute();
    const iv = setInterval(recompute, 1500);
    recompute();
    window.addEventListener('stt:recording', onRecording as any);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('stt:recording', onRecording as any);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
      try { clearInterval(iv); } catch {}
    };
  }, []);

  const toggleNotif = React.useCallback(() => {
    setNotifOpen(prev => {
      const opening = !prev;
      if (opening) {
        setOverlayReady(false);
        setChatOpen(false); setTasksOpen(false); setAiOpen(false); setGemmaTasksOpen(false);
        setPanelOrder(['notif']);
      } else {
        setOverlayReady(false);
        setPanelOrder(order => order.filter(k=>k!=='notif'));
      }
      return opening;
    });
  }, []);

  // Poll najbliższego spotkania w oknie 5 minut
  useEffect(() => {
    let mounted = true;
    const isSuppressed = (id: string | undefined | null) => {
      try { const until = Number(localStorage.getItem(`stt:suppress:${id}`) || '0'); return Date.now() < until; } catch { return false; }
    };
    const pickNext = async () => {
      try {
        const list = await getUpcomingEvents(1, 10);
        const now = Date.now();
        const FIVE = 5 * 60 * 1000;
        const TWO_HOURS = 120 * 60 * 1000;
        // Spróbuj odświeżyć bieżące
        let inProg: CalendarEvent | null = currentMeeting;
        if (!inProg) {
          for (const ev of list || []) {
            const start = ev.start ? new Date(ev.start).getTime() : NaN;
            if (Number.isNaN(start)) continue;
            const end = ev.end ? new Date(ev.end as any).getTime() : (start + TWO_HOURS);
            if (isSuppressed(ev.id)) continue;
            if (now >= start && now <= end) { inProg = ev; currentEndMsRef.current = end; break; }
          }
        }
        if (inProg) setCurrentMeeting(inProg);
        let best: CalendarEvent | null = null;
        let bestDelta = Infinity;
        for (const ev of list || []) {
          const t = ev.start ? new Date(ev.start).getTime() : NaN;
          if (Number.isNaN(t)) continue;
          if (isSuppressed(ev.id)) continue;
          const delta = t - now;
          if (delta >= 0 && delta <= FIVE && delta < bestDelta) {
            best = ev; bestDelta = delta;
          }
        }
        if (!mounted) return;
        setNextMeeting(best);
        setMeetCountdown(best ? Math.max(0, Math.floor(bestDelta / 1000)) : 0);
      } catch {}
    };
    pickNext();
    const iv = setInterval(pickNext, 30000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  // Odliczanie do startu
  useEffect(() => {
    if (!nextMeeting && !currentMeeting) return;
    const tick = () => {
      try {
        const now = Date.now();
        if (nextMeeting) {
          const t = nextMeeting.start ? new Date(nextMeeting.start).getTime() : NaN;
          if (Number.isNaN(t)) { setNextMeeting(null); return; }
          const sec = Math.max(0, Math.floor((t - now) / 1000));
          setMeetCountdown(sec);
          if (sec <= 0) {
            // przełączenie na „Trwa spotkanie”
            const TWO_HOURS = 120 * 60 * 1000;
            const endMs = nextMeeting.end ? new Date(nextMeeting.end as any).getTime() : (t + TWO_HOURS);
            currentEndMsRef.current = endMs;
            setCurrentMeeting(nextMeeting);
            setNextMeeting(null);
          }
        } else if (currentMeeting) {
          const endMs = currentEndMsRef.current;
          if (endMs && now > endMs) {
            setCurrentMeeting(null);
            currentEndMsRef.current = null;
          }
        }
      } catch {}
    };
    const iv = setInterval(tick, 1000);
    tick();
    return () => { clearInterval(iv); };
  }, [nextMeeting, currentMeeting]);

  const openTranscriptionForNext = () => {
    try {
      if (!nextMeeting?.id) return;
      const url = `/app/notes/transcription?meeting=${encodeURIComponent(nextMeeting.id)}&autostart=1`;
      window.open(url, '_blank', 'noopener');
    } catch {}
  };
  const endCurrentTranscription = () => {
    try {
      if (!currentMeeting?.id) return;
      // Wyślij sygnał do wszystkich kart, aby zatrzymały STT dla tego spotkania
      localStorage.setItem('stt:stop', JSON.stringify({ meetingId: currentMeeting.id, at: Date.now() }));
      // Zapisz tłumik (do końca spotkania) – nie pokazuj już paska po odświeżeniu
      const until = currentEndMsRef.current && currentEndMsRef.current > Date.now()
        ? currentEndMsRef.current
        : Date.now() + 2 * 60 * 60 * 1000; // fallback 2h
      try { localStorage.setItem(`stt:suppress:${currentMeeting.id}`, String(until)); } catch {}
    } catch {}
    // Ukryj monitor w topbarze do końca obecnej sesji
    setCurrentMeeting(null);
    currentEndMsRef.current = null;
    setNextMeeting(null);
    setMeetCountdown(0);
  };
  const toggleChat = React.useCallback(() => {
    setChatOpen(prev => {
      const opening = !prev;
      if (opening) {
        setOverlayReady(false);
        setNotifOpen(false); setTasksOpen(false); setAiOpen(false); setGemmaTasksOpen(false);
        setPanelOrder(['chat']);
      } else {
        setOverlayReady(false);
        setPanelOrder(order => order.filter(k=>k!=='chat'));
      }
      return opening;
    });
  }, []);
  const toggleTasks = React.useCallback(() => {
    setTasksOpen(prev => {
      const opening = !prev;
      if (opening) {
        setOverlayReady(false);
        setNotifOpen(false); setChatOpen(false); setAiOpen(false); setGemmaTasksOpen(false);
        setPanelOrder(['tasks']);
      } else {
        setOverlayReady(false);
        setPanelOrder(order => order.filter(k=>k!=='tasks'));
      }
      return opening;
    });
  }, []);
  const toggleAI = React.useCallback(() => {
    setAiOpen(prev => {
      const opening = !prev;
      if (opening) {
        setOverlayReady(false);
        setNotifOpen(false); setChatOpen(false); setTasksOpen(false); setGemmaTasksOpen(false);
        setPanelOrder(['ai']);
      } else {
        setOverlayReady(false);
        setPanelOrder(order => order.filter(k=>k!=='ai'));
      }
      return opening;
    });
  }, []);
  const toggleGemmaTasks = React.useCallback(() => {
    if (isFieldWorkerViewer) return;
    setGemmaTasksOpen((prev) => {
      const opening = !prev;
      if (opening) {
        setOverlayReady(false);
        setNotifOpen(false); setChatOpen(false); setTasksOpen(false); setAiOpen(false);
        setPanelOrder(['gemmaTasks']);
      } else {
        setOverlayReady(false);
        setPanelOrder((order) => order.filter((k) => k !== 'gemmaTasks'));
      }
      return opening;
    });
  }, [isFieldWorkerViewer]);

  // Sprawdzanie rozmiaru ekranu do trybu responsywnego
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const c = await fetchUnreadCount();
        if (mounted) {
          unreadBaseRef.current = typeof c === 'number' ? c : 0;
          setUnread(unreadBaseRef.current + manualUnreadCount());
        }
      } catch {
        if (mounted) {
          unreadBaseRef.current = 0;
          setUnread(manualUnreadCount());
        }
      }
    };
    load();
    const i = setInterval(load, 5000);
    const off = onNotificationsUnreadCount((c)=>{ if (mounted) { unreadBaseRef.current = Math.max(0, Number(c)||0); setUnread(unreadBaseRef.current + manualUnreadCount()); } });
    const onManual = () => { if (mounted) setUnread(unreadBaseRef.current + manualUnreadCount()); };
    window.addEventListener('notifications:manualUnreadChanged', onManual as any);
    return () => { mounted = false; clearInterval(i); off?.(); window.removeEventListener('notifications:manualUnreadChanged', onManual as any); };
  }, []);

  // Licznik zadań – otwarte zadania z naszej bazy (jedna lista per użytkownik + zmigrowane)
  const fetchTasksCount = React.useCallback(async () => {
    try {
      const items = await listMyTasks({ showCompleted: false });
      setTasksCount(Array.isArray(items) ? items.length : 0);
    } catch {
      setTasksCount(0);
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    fetchTasksCount();
    const i = setInterval(fetchTasksCount, 60000);
    const onChanged = () => fetchTasksCount();
    window.addEventListener('tasks:changed', onChanged as any);
    return () => { clearInterval(i); window.removeEventListener('tasks:changed', onChanged as any); };
  }, [isAuthenticated, isLoading, fetchTasksCount]);

  const fetchGemmaQueueActiveCount = React.useCallback(async () => {
    try {
      const jobs = await fetchGemmaQueueJobs();
      const n = jobs.filter((j) => j.status === 'queued' || j.status === 'paused' || j.status === 'running').length;
      setGemmaQueueActiveCount(n);
    } catch {
      setGemmaQueueActiveCount(0);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || isLoading || isFieldWorkerViewer) {
      setGemmaQueueActiveCount(0);
      return;
    }
    void fetchGemmaQueueActiveCount();
    const onGemmaQueueChanged = () => void fetchGemmaQueueActiveCount();
    window.addEventListener('gemma-queue-changed', onGemmaQueueChanged);
    const interval = setInterval(() => void fetchGemmaQueueActiveCount(), 60000);
    return () => {
      window.removeEventListener('gemma-queue-changed', onGemmaQueueChanged);
      clearInterval(interval);
    };
  }, [isAuthenticated, isLoading, isFieldWorkerViewer, fetchGemmaQueueActiveCount]);

  useEffect(() => {
    if (!isFieldWorkerViewer) return;
    setGemmaTasksOpen(false);
    setAiOpen(false);
    setPanelOrder((prev) => prev.filter((k) => k !== 'gemmaTasks' && k !== 'ai'));
  }, [isFieldWorkerViewer]);

  // Globalny skrót do otwierania panelu powiadomień (emitowany z Layout)
  useEffect(() => {
    const toggleHandler = () => {
      setNotifOpen(prev => {
        const opening = !prev;
        if (opening) {
          setOverlayReady(false);
          setChatOpen(false); setTasksOpen(false); setAiOpen(false); setGemmaTasksOpen(false);
          setPanelOrder(['notif']);
          setTimeout(() => setOverlayReady(true), 10);
        } else {
          setOverlayReady(false);
          setPanelOrder(order => order.filter(k=>k!=='notif'));
        }
        return opening;
      });
    };
    window.addEventListener('ui:notifications:toggle', toggleHandler as any);
    return () => window.removeEventListener('ui:notifications:toggle', toggleHandler as any);
  }, []);

  // Presence: heartbeat (wszyscy zalogowani)
  const isAdmin = user?.role === 'admin';
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    let mounted = true;
    const beat = async () => { try { await sendHeartbeat(); } catch {} };
    beat();
    // W dev podnieś interwał, aby ograniczyć szum logów
    const interval = process.env.NODE_ENV === 'development' ? 60000 : 20000;
    const hb = setInterval(beat, interval);
    return () => { mounted = false; clearInterval(hb); };
  }, [isAuthenticated, isLoading]);

  // Presence: polling listy online (wszyscy zalogowani)
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    let mounted = true;
    const load = async () => {
      try {
        const list = await fetchOnlineUsers();
        if (mounted) setOnlineUsers(list);
      } catch {
        if (mounted) setOnlineUsers([]);
      }
    };
    load();
    const interval = process.env.NODE_ENV === 'development' ? 60000 : 20000;
    const pl = setInterval(load, interval);
    return () => { mounted = false; clearInterval(pl); };
  }, [isAuthenticated, isLoading]);

  // Odbieranie próśb o udostępnienie ekranu (dla wszystkich zalogowanych)
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    onScreenshareRequest((fromUserId) => {
      setRequestFromUserId(fromUserId);
      setConsentOpen(true);
    });
  }, [isAuthenticated, isLoading]);

  // Po akceptacji: utwórz RTCPeerConnection, dołącz stream z getDisplayMedia i wyślij offer
  const handleConsentAccept = async () => {
    if (!requestFromUserId) return;
    try {
      const media = await (navigator.mediaDevices as any).getDisplayMedia({ video: true, audio: false });
      const pc = new RTCPeerConnection({ iceServers: [ { urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' } ] });
      pcRef.current = pc;
      media.getTracks().forEach((t: MediaStreamTrack) => pc.addTrack(t, media));
      // Rejestrowanie zmian checkboxów i emit do obserwatorów
      try {
        const s = getSocket();
        const onChange = (e: Event) => {
          const target = e.target as HTMLInputElement;
          if (target && target.type === 'checkbox') {
            // Wyznacz ścieżkę CSS (data-key jeżeli istnieje, inaczej selektor fallback)
            const path = target.getAttribute('data-key') || (() => {
              try {
                const name = target.getAttribute('name');
                if (name) return `input[type="checkbox"][name="${name}"]`;
                const id = target.getAttribute('id');
                if (id) return `#${id}`;
              } catch {}
              return '';
            })();
            const rect = target.getBoundingClientRect();
            // Wskaż też pozycję kursora jako fallback
            s.emit('dom:checkbox', { path, checked: target.checked, x: Math.round(rect.left + rect.width/2), y: Math.round(rect.top + rect.height/2) });
          }
        };
        document.addEventListener('change', onChange, true);
      } catch {}
      pc.onicecandidate = (e) => { if (e.candidate) sendIce(requestFromUserId, e.candidate); };
      onAnswer((fromUserId, sdp) => { if (fromUserId === requestFromUserId) pc.setRemoteDescription(new RTCSessionDescription(sdp)); });
      onIce((fromUserId, candidate) => { if (fromUserId === requestFromUserId) pc.addIceCandidate(new RTCIceCandidate(candidate)); });
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendOffer(requestFromUserId, offer);
    } catch (e) {
      // user mógł anulować – nic nie robimy
    } finally {
      setConsentOpen(false);
    }
  };

  // Globalny toggle overlay sidebara (hamburger) + synchronizacja z obserwowanym
  useEffect(() => {
    const toggleOverlay = () => setOverlayOpen(prev => !prev);
    window.addEventListener('ui:overlay:toggle', toggleOverlay as any);
    const s = getSocket();
    const onOverlaySet = ({ open }: any) => { setOverlayOpen(!!open); };
    s.on('ui:overlay:set', onOverlaySet);
    return () => { window.removeEventListener('ui:overlay:toggle', toggleOverlay as any); s.off('ui:overlay:set', onOverlaySet); };
  }, []);

  const handleLoginClick = () => {
    navigate('/login');
  };

  // Nasłuchiwanie zmian widoczności sidebara (z karty Widok)
  useEffect(() => {
    const handler = (e: Event) => setIsSidebarVisible((e as CustomEvent).detail?.visible);
    window.addEventListener('ui:sidebar:toggle', handler as any);
    return () => window.removeEventListener('ui:sidebar:toggle', handler as any);
  }, []);

  // Obliczanie marginesu i szerokości nagłówka
  const getHeaderStyles = () => ({ width: '100%', ml: 0 });

  const [searchValue, setSearchValue] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [callMenuAnchor, setCallMenuAnchor] = useState<HTMLElement | null>(null);
  const [tokens, setTokens] = useState<string[]>([]);
  const [overlayItems, setOverlayItems] = useState<SearchItem[]>([]);
  const [selectedIdx, setSelectedIdx] = useState<number>(-1);
  const [incomingOpen, setIncomingOpen] = useState(false);
  const [incomingFrom, setIncomingFrom] = useState<string | null>(null);
  const [incomingAvatar, setIncomingAvatar] = useState<string | null>(null);
  const [incomingMeet, setIncomingMeet] = useState<string | null>(null);
  const [incomingLeftSec, setIncomingLeftSec] = useState<number>(30);
  const [missedCalls, setMissedCalls] = useState<number>(0);
  const [incomingUserId, setIncomingUserId] = useState<string | null>(null);
  const [incomingCallKey, setIncomingCallKey] = useState<string | null>(null);
  const [incomingIsConference, setIncomingIsConference] = useState<boolean>(false);
  const [incomingParticipantsCount, setIncomingParticipantsCount] = useState<number>(0);
  const [missedList, setMissedList] = useState<Array<{ userId: string; displayName: string; avatarUrl: string | null; at: string }>>([]);
  const [conferenceOpen, setConferenceOpen] = useState(false);
  const [conferenceStarting, setConferenceStarting] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [editingChannelId, setEditingChannelId] = useState<string | null>(null);
  const [editingChannelTitle, setEditingChannelTitle] = useState<string>('');
  const [chatFilter, setChatFilter] = useState<string>(''); // filtr listy pokoi/użytkowników w panelu chatu
  type ToastItem = { id: string; text: string; room: string | null; from: string | null; avatar: string | null; imageUrl?: string | null; createdAt?: string | null; messageId?: string; convId?: string; convType?: 'direct'|'group'; fromUserId?: string | null };
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const displayedMsgIdsRef = useRef<Set<string>>(new Set());
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiHasNew, setAiHasNew] = useState(false);
  const [tasksCount, setTasksCount] = useState<number>(0);
  // Refs aktualnego stanu – potrzebne, żeby handler klawiatury miał świeże wartości
  const notifOpenRef = useRef<boolean>(false);
  const chatOpenRef = useRef<boolean>(false);
  const tasksOpenRef = useRef<boolean>(false);
  const aiOpenRef = useRef<boolean>(false);
  const gemmaTasksOpenRef = useRef<boolean>(false);
  const panelOrderRef = useRef<Array<'notif'|'chat'|'tasks'|'ai'|'gemmaTasks'>>([]);
  useEffect(() => { notifOpenRef.current = notifOpen; }, [notifOpen]);
  useEffect(() => { chatOpenRef.current = chatOpen; }, [chatOpen]);
  useEffect(() => { tasksOpenRef.current = tasksOpen; }, [tasksOpen]);
  useEffect(() => { aiOpenRef.current = aiOpen; }, [aiOpen]);
  useEffect(() => { gemmaTasksOpenRef.current = gemmaTasksOpen; }, [gemmaTasksOpen]);
  useEffect(() => { panelOrderRef.current = panelOrder; }, [panelOrder]);

  const openAiDrawer = useCallback(() => {
    if (isFieldWorkerViewer) return;
    setGemmaTasksOpen(false);
    setOverlayReady(false);
    setAiOpen(true);
    setAiHasNew(false);
    setPanelOrder((prev) => {
      const noAi = prev.filter((k) => k !== 'ai' && k !== 'gemmaTasks');
      if (chatOpenRef.current) {
        if (noAi.includes('chat')) return [...noAi, 'ai'];
        return ['chat', 'ai'];
      }
      return noAi.length ? [...noAi, 'ai'] : ['ai'];
    });
  }, [isFieldWorkerViewer]);

  const [gemmaAvailableTopbar, setGemmaAvailableTopbar] = useState(false);
  useEffect(() => {
    if (!isAuthenticated || isLoading || isFieldWorkerViewer) {
      if (isFieldWorkerViewer) setGemmaAvailableTopbar(false);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      const d = await fetchAgentAiStatus();
      if (cancelled) return;
      setGemmaAvailableTopbar(!!d?.gemmaAvailable);
    };
    tick();
    const id = window.setInterval(tick, 120000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [isAuthenticated, isLoading, isFieldWorkerViewer]);

  // Wystaw globalny znacznik informujący Layout, że czat jest otwarty
  useEffect(() => {
    try { (window as any).__unitrescoChatOpen = chatOpen; } catch {}
    return () => { try { (window as any).__unitrescoChatOpen = false; } catch {} };
  }, [chatOpen]);
  const [chatSpaces, setChatSpaces] = useState<ChatSpace[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [activeChatSpace, setActiveChatSpace] = useState<ChatSpace | null>(null);
  const [activeChatUser, setActiveChatUser] = useState<{ email?: string; display_name?: string } | null>(null);
  const [roomUnreads, setRoomUnreads] = useState<Record<string, number>>({});
  const [allUsers, setAllUsers] = useState<Array<{ user_id: string; display_name?: string | null; email?: string | null; avatar_url?: string | null; role?: string | null }>>([]);
  const [fieldWorkersExpanded, setFieldWorkersExpanded] = useState(true);
  const [dmExpanded, setDmExpanded] = useState(true);
  const [roomsCollapsed, setRoomsCollapsed] = useState(false); // false => rozwinięte
  const fieldWorkersGroupChannel = React.useMemo(
    () => (chatSpaces as any[] | null | undefined)?.find(
      (c: any) => c?.type === 'group' && isFieldWorkersGroupChannelName(c?.name)
    ) ?? null,
    [chatSpaces]
  );
  // Lokalny cache listy pokoi, aby UI wyświetlił się natychmiast
  function loadSpacesFromCache(): ChatSpace[] {
    try {
      const raw = localStorage.getItem('chat:spaces:v1');
      if (!raw) return [];
      const { items } = JSON.parse(raw) as { ts: number; items: any[] };
      const arr = Array.isArray(items) ? items : [];
      return arr.filter((s:any)=> s?.type==='group' || (s?.name && s.name.toLowerCase()==='wiadomości ogólne')) as any;
    } catch { return []; }
  }
  function saveSpacesToCache(items: ChatSpace[]) {
    try {
      const onlyGroups = (items as any).filter((s:any)=> s?.type==='group' || (s?.name && s.name.toLowerCase()==='wiadomości ogólne'));
      localStorage.setItem('chat:spaces:v1', JSON.stringify({ ts: Date.now(), items: onlyGroups }));
    } catch {}
  }
  function sortRooms(items: ChatSpace[]): ChatSpace[] {
    const isGeneral = (s?: string) => (s || '').toLowerCase() === 'wiadomości ogólne';
    return [...items].sort((a,b) => {
      const ag = isGeneral(a?.name);
      const bg = isGeneral(b?.name);
      if (ag && !bg) return -1;
      if (!ag && bg) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }

  // Realtime: nasłuchuj połączeń przychodzących
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    const onDnd = (e: any) => { /* nic – stan brany z localStorage w momencie zdarzenia */ };
    window.addEventListener('chat:dnd', onDnd as any);
    const off = onIncomingCall(({ fromUserId, fromDisplayName, fromAvatarUrl, meetLink, toUserId, isConference, participantsCount }) => {
      const dnd = (localStorage.getItem('chat:dnd') || '0') === '1';
      if (dnd) return; // DND: blokuj połączenia przychodzące
      // Dźwięk/okno tylko u zapraszanego użytkownika
      if (!user?.user_id || (toUserId && toUserId !== user.user_id)) return;
      setIncomingUserId(fromUserId || null);
      setIncomingFrom(fromDisplayName || fromUserId || 'Użytkownik');
      setIncomingAvatar(fromAvatarUrl || null);
      setIncomingMeet(meetLink);
      setIncomingCallKey(buildCallKey({ meetLink, fromUserId, toUserId: toUserId || user?.user_id }));
      setIncomingIsConference(!!isConference);
      setIncomingParticipantsCount(typeof participantsCount === 'number' ? participantsCount : 0);
      setIncomingOpen(true);
      setIncomingLeftSec(30);
    });
    // Zamknij okno, gdy inna zakładka odbierze/odrzuci to samo połączenie.
    const offResolved = onCallResolvedFromOtherTab(({ callKey }) => {
      setIncomingCallKey((current) => {
        if (current && current === callKey) {
          setIncomingOpen(false);
          setIncomingFrom(null);
          setIncomingAvatar(null);
          setIncomingMeet(null);
          setIncomingUserId(null);
          setIncomingIsConference(false);
          setIncomingParticipantsCount(0);
          return null;
        }
        return current;
      });
    });
    return () => {
      try { (off as any)?.(); } catch {}
      try { offResolved(); } catch {}
      window.removeEventListener('chat:dnd', onDnd as any);
    };
  }, [isAuthenticated, isLoading]);

  const clearIncomingState = () => {
    setIncomingOpen(false);
    setIncomingFrom(null);
    setIncomingAvatar(null);
    setIncomingMeet(null);
    setIncomingCallKey(null);
    setIncomingUserId(null);
    setIncomingIsConference(false);
    setIncomingParticipantsCount(0);
  };

  const handleAcceptIncoming = () => {
    if (incomingMeet) {
      window.open(incomingMeet, '_blank', 'noopener');
    }
    if (incomingCallKey) broadcastCallResolved(incomingCallKey, 'accepted');
    clearIncomingState();
  };
  const handleDeclineIncoming = () => {
    if (incomingCallKey) broadcastCallResolved(incomingCallKey, 'declined');
    const prevUserId = incomingUserId;
    const prevFrom = incomingFrom;
    const prevAvatar = incomingAvatar;
    clearIncomingState();
    setMissedCalls((v) => v + 1);
    // dopisz do listy nieodebranych
    if (prevUserId) {
      setMissedList((list) => [{ userId: prevUserId, displayName: prevFrom || 'Użytkownik', avatarUrl: prevAvatar, at: new Date().toISOString() }, ...list].slice(0, 10));
    }
  };

  // Auto-wygaszanie połączenia przychodzącego po 30s, jeśli użytkownik nie zareaguje
  useEffect(() => {
    if (!incomingOpen) return;
    const start = Date.now();
    const endAt = start + 30000;
    const tick = () => {
      const left = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
      setIncomingLeftSec(left);
      if (left <= 0) {
        handleDeclineIncoming();
        return;
      }
      rafId = requestAnimationFrame(tick);
    };
    let rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [incomingOpen]);

  const handleOpenCallMenu = (e: React.MouseEvent<HTMLElement>) => {
    setCallMenuAnchor(e.currentTarget);
    // wyczyszczenie licznika nieodebranych przy otwarciu menu połączeń
    // nie zerujemy od razu – pokażemy panel nieodebranych, a po kliknięciu „Oddzwoń” lub zamknięciu panelu możemy wyczyścić
  };
  const handleCallbackMissed = async (userId: string) => {
    await handleStartCall(userId);
    setMissedList((list) => list.filter((m) => m.userId !== userId));
    setMissedCalls((v) => Math.max(0, v - 1));
  };

  // (Users tab removed; persistent room link feature removed)
  // Ładuj pokoje po otwarciu szuflady chatu
  useEffect(() => {
    if (!chatOpen) return;
    let mounted = true;
    setChatLoading(true);
    const failSafe = setTimeout(() => { if (mounted) setChatLoading(false); }, 8000);
    // Nie pokazuj już cache'u (by nie migały stare liczniki); czekamy na świeże dane z backendu
    setChatSpaces([]);
    listSpaces()
      .then((spaces:any)=>{ if(mounted){ const s = (spaces||[]) as any[]; setChatSpaces(s as any); saveSpacesToCache(s as any); const total = (s||[]).reduce((acc:number, c:any)=> acc + Number(c.unreadCount || 0), 0); setUnreadChat(total); } })
      .catch((e)=>{ if(mounted){ setChatSpaces([]); console.warn('chat spaces error', e?.message || e); } })
      .finally(()=>{ if(mounted){ clearTimeout(failSafe); setChatLoading(false); } });
    // Załaduj listę użytkowników (do DM)
    listUsersPublic().then(u => { if (mounted) setAllUsers(u as any); }).catch(()=>{});
    return () => { mounted = false; clearTimeout(failSafe); };
  }, [chatOpen]);

  // Prefetch cache w tle co 60s, kiedy chat jest zamknięty – nie używamy cache do badge'y
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    if (chatOpen) return;
    let cancelled = false;
    async function prefetch() {
      try {
        const spaces:any = await listSpaces();
        if (!cancelled) saveSpacesToCache(spaces||[]);
      } catch {}
    }
    prefetch();
    const id = setInterval(prefetch, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAuthenticated, isLoading, chatOpen]);

  // Przelicz liczniki per pokój – po otwarciu panelu pobieramy z backendu unreadCount i sumujemy
  useEffect(() => {
    if (!chatOpen) return;
    let stopped = false;
    async function refresh() {
      try {
        const spaces:any = await listSpaces();
        setChatSpaces(spaces||[]);
        const map: Record<string, number> = {};
        (spaces||[]).forEach((c:any)=>{ map[c.id] = Number(c.unreadCount || 0); });
        if (!stopped) {
          setRoomUnreads(map);
          // przelicz globalny badge jako sumę wszystkich rozmów
          const total = Object.values(map).reduce((a,b) => a + (b || 0), 0);
          setUnreadChat(total);
        }
      } catch {}
    }
    refresh();
    const id = setInterval(refresh, 15000);
    return () => { stopped = true; clearInterval(id); };
  }, [chatOpen]);

  // Poll unreads when chat is closed – sumuj z backendu, aby zgadzało się z listą
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    let cancelled = false;
    async function poll() {
      if (chatOpen) return;
      try {
        const spaces:any = await listSpaces();
        const total = (spaces||[]).reduce((acc:number, c:any)=> acc + Number(c.unreadCount || 0), 0);
        if (!cancelled) setUnreadChat(total);
      } catch {}
    }
    poll();
    const id = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [chatOpen, isAuthenticated, isLoading, user?.email, user?.display_name, user?.first_name, user?.last_name]);
  const handleCloseCallMenu = () => setCallMenuAnchor(null);
  const handleStartCall = async (targetUserId: string) => {
    try {
      const { meetLink } = await startCall(targetUserId);
      // Otwórz spotkanie natychmiast u dzwoniącego
      window.open(meetLink, '_blank', 'noopener');
    } catch (e) {
      // opcjonalnie: snackbar/toast
      console.error('startCall error', e);
    } finally {
      handleCloseCallMenu();
    }
  };
  const handleOpenConference = () => {
    handleCloseCallMenu();
    setConferenceOpen(true);
  };
  const handleStartConference = async (userIds: string[]) => {
    if (!userIds || userIds.length === 0) return;
    try {
      setConferenceStarting(true);
      const { meetLink } = await startConferenceCall(userIds);
      window.open(meetLink, '_blank', 'noopener');
      setConferenceOpen(false);
    } catch (e) {
      console.error('startConferenceCall error', e);
      try {
        showNotification?.('Nie udało się uruchomić telekonferencji', 'error' as any);
      } catch {}
    } finally {
      setConferenceStarting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchValue.trim();
    if (q.length === 0) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };
  // Tokenizacja: /projekty, /offers etc. z TAB na chip
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchValue(val);
    setAnchorEl(e.currentTarget as any);
    // prosta propozycja autouzupełnień prefiksów
    const lower = val.toLowerCase();
    const suggestions: SearchItem[] = [];
    const presets = [
      { id: 'scope-projects', key: '/projekty', label: 'projekty' },
      { id: 'scope-offers', key: '/oferty', label: 'oferty' },
      { id: 'scope-database', key: '/kontrahenci', label: 'kontrahenci' },
    ];
    presets.forEach(p => {
      if (p.key.startsWith(lower) && lower !== p.key) {
        suggestions.push({ id: p.id, primary: p.label, onSelect: () => applyToken(p.key) });
      }
    });
    setOverlayItems(suggestions);
    setSelectedIdx(suggestions.length > 0 ? 0 : -1);
  };

  const applyToken = (token: string) => {
    const nt = [...tokens, token];
    setTokens(nt);
    setSearchValue('');
    // po ustawieniu tokenu, schowaj overlay
    setOverlayItems([]);
    setSelectedIdx(-1);
  };

  // Obsługa nawigacji po podpowiedziach i akceptacji tokenów – na keyDown, aby zablokować domyślne ruchy kursora
  const handleSuggestionsKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // ESC obsługujemy w handleSearchKeyDown
    if (overlayItems.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault(); e.stopPropagation();
        setSelectedIdx((prev) => (prev + 1) % overlayItems.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault(); e.stopPropagation();
        setSelectedIdx((prev) => (prev - 1 + overlayItems.length) % overlayItems.length);
        return;
      }
      if (e.key === 'Enter' && selectedIdx >= 0 && overlayItems[selectedIdx]) {
        e.preventDefault(); e.stopPropagation();
        overlayItems[selectedIdx].onSelect();
        return;
      }
    }

    if (e.key === 'Tab' && searchValue.startsWith('/')) {
      e.preventDefault(); e.stopPropagation();
      const first = selectedIdx >= 0 ? overlayItems[selectedIdx] : overlayItems[0];
      if (first) {
        first.onSelect();
      } else {
        applyToken(searchValue);
      }
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace usuwa ostatni chip, gdy pole jest puste
    if (e.key === 'Backspace' && searchValue.length === 0 && tokens.length > 0) {
      e.preventDefault(); e.stopPropagation();
      setTokens((prev) => prev.slice(0, -1));
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLInputElement).blur();
      setOverlayItems([]);
      return;
    }
    handleSuggestionsKeyDown(e);
  };

  // Globalne fokusowanie paska wyszukiwania
  useEffect(() => {
    const onFocusSearch = () => {
      try {
        searchInputRef.current?.focus();
        searchInputRef.current?.select?.();
      } catch {}
    };
    window.addEventListener('ui:search:focus', onFocusSearch as any);
    // Listenery stanu AI: generowanie w tle i gotowa odpowiedź
    const onBusy = (e: Event) => {
      const busy = (e as CustomEvent)?.detail?.busy;
      setAiBusy(!!busy);
      if (busy) setAiHasNew(false);
    };
    const onReady = () => {
      setAiBusy(false);
      if (!aiOpen) setAiHasNew(true);
    };
    window.addEventListener('ai:busy', onBusy as any);
    window.addEventListener('ai:ready', onReady as any);
    // Skrót: "c" otwiera chat, ale nie gdy fokus jest w polu edycji/textarea lub gdy jest aktywny modifier
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || (e.target as HTMLElement)?.isContentEditable;
      const noMods = !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey;
      // ESC – zawsze działa, także gdy fokus jest w polu edycyjnym
      if (e.key === 'Escape') {
        e.preventDefault();
        // Jeśli fokus jest w głównym filtrze strony lub filtrze zadań – tylko odfokusuj
        try {
          const active = document.activeElement as HTMLElement | null;
          const role = active?.getAttribute?.('data-role');
          if (role === 'page-filter' || role === 'tasks-filter') {
            e.stopPropagation();
            (active as HTMLInputElement)?.blur?.();
            return;
          }
        } catch {}
        // Jeśli którykolwiek panel zgłosi blokadę ESC (np. edytor dodawania zadania), nie zamykaj top okna
        const escBlocked = (()=>{ try { return !!(window as any).__unitrescoTasksComposerVisible; } catch { return false; }})();
        if (escBlocked) return;
        const notif = notifOpenRef.current;
        const chat = chatOpenRef.current;
        const tasks = tasksOpenRef.current;
        const ai = aiOpenRef.current;
        const gemmaTasks = gemmaTasksOpenRef.current;
        const order = panelOrderRef.current;
        const active = order.filter(k => (k==='notif'&&notif)||(k==='chat'&&chat)||(k==='tasks'&&tasks)||(k==='ai'&&ai)||(k==='gemmaTasks'&&gemmaTasks));
        const top = active[active.length - 1];
        if (top === 'notif') { setNotifOpen(false); setPanelOrder(prev => prev.filter(k=>k!=='notif')); return; }
        if (top === 'chat') { setChatOpen(false); setActiveChatSpace(null); setActiveChatUser(null); setPanelOrder(prev => prev.filter(k=>k!=='chat')); return; }
        if (top === 'tasks') { setTasksOpen(false); setPanelOrder(prev => prev.filter(k=>k!=='tasks')); return; }
        if (top === 'ai') { setAiOpen(false); setPanelOrder(prev => prev.filter(k=>k!=='ai')); return; }
        if (top === 'gemmaTasks') { setGemmaTasksOpen(false); setPanelOrder(prev => prev.filter(k=>k!=='gemmaTasks')); return; }
        // Jeśli kolejkowanie nie istnieje, zamykaj wg priorytetu
        if (chat) { setChatOpen(false); setActiveChatSpace(null); setActiveChatUser(null); return; }
        if (tasks) { setTasksOpen(false); return; }
        if (ai) { setAiOpen(false); return; }
        if (gemmaTasks) { setGemmaTasksOpen(false); return; }
        if (notif) { setNotifOpen(false); return; }
      }
      // skróty literowe obsłużymy na keyup, by uniknąć auto‑repeat/migania
    };
    const onKeyUp = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || (e.target as HTMLElement)?.isContentEditable;
      const noMods = !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey;
      if (!isTyping && noMods) {
        if (e.code === 'KeyC' || e.key === 'c' || e.key === 'C') { e.preventDefault(); toggleChat(); return; }
        if (e.code === 'KeyT' || e.key === 't' || e.key === 'T') { e.preventDefault(); toggleTasks(); return; }
        if (e.code === 'KeyD' || e.key === 'd' || e.key === 'D') {
          // Jeśli czat jest otwarty, nie wykonuj akcji skrótu D
          if (chatOpenRef.current) {
            return;
          }
          // Jeśli okno "Add offer by AI" jest otwarte, nie wykonuj akcji skrótu D
          const addOfferAIOpen = (() => { try { return !!(window as any).__unitrescoAddOfferAIOpen; } catch { return false; } })();
          if (addOfferAIOpen) {
            return;
          }
          e.preventDefault();
          try {
            // Priorytet: jeśli otwarte „Moje zadania", to dodaj zadanie
            if (tasksOpenRef.current) {
              const tasksBtn = document.querySelector('[data-action="tasks:add"]') as HTMLElement | null;
              if (tasksBtn) { tasksBtn.click(); return; }
            }
            // W przeciwnym razie: ogólny przycisk dodawania na bieżącej stronie
            const addBtn = document.querySelector('[data-action="add"]') as HTMLElement | null;
            if (addBtn) { addBtn.click(); return; }
          } catch {}
          return;
        }
        if (e.code === 'KeyF' || e.key === 'f' || e.key === 'F') {
          e.preventDefault();
          try {
            // Jeśli otwarty rejestr zdarzeń – fokusuj jego filtr
            if (notifOpenRef.current) {
              const input = document.querySelector<HTMLInputElement>('[data-role="notifications-filter"]');
              if (input) { input.focus(); try { input.select?.(); } catch {} return; }
            }
            // Priorytet: jeśli otwarte „Moje zadania”, fokusuj filtr zadań
            if (tasksOpenRef.current) {
              const input = document.querySelector<HTMLInputElement>('[data-role="tasks-filter"]');
              if (input) { input.focus(); try { input.select?.(); } catch {} return; }
            }
            // Fokusuj główny filtr strony (projekty/oferty/faktury)
            const pageInput = document.querySelector<HTMLInputElement>('[data-role="page-filter"]');
            if (pageInput) { pageInput.focus(); try { pageInput.select?.(); } catch {} return; }
          } catch {}
          return;
        }
        // 'n' obsługuje Layout przez event 'ui:notifications:toggle' (unikamy podwójnego toggle)
      }
    };
    // keydown: bez capture – pozwól komponentom (np. TasksDrawer) zablokować ESC wcześniej
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('ui:search:focus', onFocusSearch as any);
      window.removeEventListener('keydown', onKey as any);
      window.removeEventListener('keyup', onKeyUp as any);
      window.removeEventListener('ai:busy', onBusy as any);
      window.removeEventListener('ai:ready', onReady as any);
    };
  }, []);

  // Gdy panel AI zostanie otwarty (dowolną ścieżką), usuń wskaźnik nowej odpowiedzi
  useEffect(() => {
    if (aiOpen) setAiHasNew(false);
  }, [aiOpen]);

  function pushToast(t: { text: string; room: string | null; from: string | null; avatar: string | null; imageUrl?: string | null; createdAt?: string | null; messageId?: string; convId?: string; convType?: 'direct'|'group'; fromUserId?: string | null }) {
    if (t.messageId && displayedMsgIdsRef.current.has(t.messageId)) {
      return; // uniknij duplikatu (np. new-message + mention tego samego msg)
    }
    if (t.messageId) {
      displayedMsgIdsRef.current.add(t.messageId);
      setTimeout(() => { try { if (t.messageId) displayedMsgIdsRef.current.delete(t.messageId); } catch {} }, 15000);
    }
    setToasts(prev => [...prev, { id: Math.random().toString(36).slice(2), ...t }]);
    // Dźwięk: useInternalChatNotifySound w Layout / MobileLayout (Header nie jest na wąskim widoku)
  }
  function closeToast(id: string) {
    setToasts(prev => prev.filter(x => x.id !== id));
  }

  // Realtime: nowe wiadomości/wzmianki – dymek pokazuj tylko gdy chat jest zamknięty
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    const off1 = onInternalChatNewMessage((p) => {
      const dnd = (localStorage.getItem('chat:dnd') || '0') === '1';
      if (dnd) { setUnreadChat(v=>v+1); return; } // tylko suma, bez dymka
      if (chatOpen) { setUnreadChat(v=>v+1); return; } // chat otwarty – nie pokazuj dymka
      const base = (api as any).defaults?.baseURL || 'http://localhost:5000/api';
      const apiBase = base.replace(/\/?$/, '');
      const isImage = typeof p.content === 'string' && p.content.startsWith('/uploads/');
      const imageUrl = isImage ? (apiBase + String(p.content)) : null;
      pushToast({ text: isImage ? 'Załącznik' : (p.content || 'Nowa wiadomość'), imageUrl, createdAt: p.createdAt || undefined, room: p.type === 'group' ? (p.roomTitle || 'Pokój') : 'Wiadomość prywatna', from: p.senderDisplayName || null, avatar: p.senderAvatarUrl || null, messageId: p.messageId, convId: p.conversationId, convType: p.type, fromUserId: p.fromUserId });
      setUnreadChat((v)=> v + 1);
    });
    const off2 = onInternalChatMention((p) => {
      const dnd = (localStorage.getItem('chat:dnd') || '0') === '1';
      if (dnd) { setUnreadChat(v=>v+1); return; }
      if (chatOpen) { setUnreadChat(v=>v+1); return; }
      const base = (api as any).defaults?.baseURL || 'http://localhost:5000/api';
      const apiBase = base.replace(/\/?$/, '');
      const isImage = typeof p.content === 'string' && p.content.startsWith('/uploads/');
      const imageUrl = isImage ? (apiBase + String(p.content)) : null;
      pushToast({ text: isImage ? 'Załącznik' : (p.content || 'Wzmianka'), imageUrl, createdAt: (p as any).createdAt || undefined, room: p.roomTitle || 'Wzmianka', from: null, avatar: null, messageId: p.messageId, convId: p.conversationId, convType: 'group', fromUserId: p.fromUserId });
      setUnreadChat((v)=> v + 1);
    });
    return () => { try { (off1 as any)?.(); } catch {}; try { (off2 as any)?.(); } catch {} };
  }, [isAuthenticated, isLoading, chatOpen]);

  // Jednolity renderer badge'a dla ikon topbara
  const renderBadge = (count: number, scale: number = 1, fontPx?: number) => {
    const baseSize = (count || 0) > 9 ? 18 : 14;
    const size = Math.round(baseSize * scale);
    const fontSize = fontPx ?? Math.round(9 * scale); // Roboto Regular domyślnie 9px
    return (
      <Box sx={{
        position: 'absolute',
        top: -2, // przesunięcie w dół, aby znaczek nie był przycięty
        right: -4,
        backgroundColor: 'error.main',
        color: 'common.white',
        borderRadius: '999px',
        minWidth: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Roboto, sans-serif',
        fontSize: fontSize,
        fontWeight: 400,
        lineHeight: 1,
        boxShadow: '0 0 0 2px #fff',
        p: 0
      }}>
        {count}
      </Box>
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <OverlaySidebar open={overlayOpen} onClose={() => setOverlayOpen(false)} />
      <AppBar 
        position="fixed" 
        color="default" 
        elevation={0} 
        sx={{ 
          top: 'var(--legacy-host-banner-height, 0px)',
          backgroundColor: (theme) => theme.palette.background.paper,
          color: (theme) => theme.palette.text.primary,
          zIndex: 2147483500, // Wyższy niż backdrop drawer'ów (2147483000-1), ale niższy niż sam drawer
          ...getHeaderStyles(),
          transition: (theme: any) => theme.transitions.create(['width', 'margin-left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar sx={{ pl: { xs: '11.5px', sm: '11.5px' }, pr: { xs: 1, sm: 1 }, display: 'grid', gridTemplateColumns: { xs: 'auto 1fr auto', sm: 'auto 1fr auto' }, alignItems: 'center', columnGap: 1, minHeight: { xs: 56, sm: 52 } }}>
          {/* Lewa kolumna – hamburger + logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifySelf: 'start' }}>
        <IconButton aria-label="menu" onClick={() => { setOverlayOpen(true); try { getSocket().emit('ui:overlay:set', { open: true }); } catch {} }} sx={{ width: 21, height: 21, borderRadius: '4px', mr: '24px' }}>
          <Box component="img" src="/assets/topbar/menu.svg" alt="menu" />
            </IconButton>
            <Box 
              component={Link}
              to="/"
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textDecoration: 'none',
                mr: '24px'
              }}
            >
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: '18px',
                  lineHeight: '21px',
                  fontWeight: 600,
                  color: '#c53030',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  margin: 0
                }}
              >
                UNITRESCO
              </Typography>
            </Box>
            {isAuthenticated && !isLoading && (
              <Paper
                component="form"
                onSubmit={handleSubmit}
                elevation={0}
                sx={{
                  px: 0,
                  py: 0,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #ededed',
                  height: 36,
                  width: 300,
                  '&:hover': {
                    borderColor: '#B9B9B9'
                  }
                }}
              >
            <Box component="img" src="/assets/topbar/search.svg" alt="search" sx={{ ml: 1.5 }} />
                {tokens.map((tkn, idx) => (
                  <Chip key={`${tkn}-${idx}`} label={tkn.replace('/', '')} size="small" sx={{ mr: 1, bgcolor: '#dbeafe', color: '#1e3a8a', fontWeight: 600 }} onDelete={() => setTokens(tokens.filter((_, i) => i !== idx))} />
                ))}
                <InputBase
                  placeholder="Szukaj w systemie"
                  value={searchValue}
                  onChange={handleSearchChange}
                  sx={{ pl: 1.5, pr: 1.5, flex: 1, fontSize: 14 }}
                  inputProps={{ 'aria-label': 'global search' }}
                  inputRef={searchInputRef}
                  onKeyDown={handleSearchKeyDown}
                />
              </Paper>
            )}
            {isAuthenticated && googleLogoutCountdown !== null && googleLogoutCountdown > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#C53030', 
                    fontWeight: 600,
                    fontSize: '13px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Automatyczne wylogowanie za: {formatTime(googleLogoutCountdown)}
                </Typography>
              </Box>
            )}
            {isAuthenticated && idleLogoutCountdown !== null && idleLogoutCountdown > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, gap: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#C53030', 
                    fontWeight: 600,
                    fontSize: '13px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Automatyczne wylogowanie za: {formatTime(idleLogoutCountdown)}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={resetIdleTimer}
                  sx={{
                    ml: 1,
                    minWidth: 'auto',
                    px: 1.5,
                    fontSize: '12px',
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Przedłuż
                </Button>
              </Box>
            )}
            {(currentMeeting || (nextMeeting && meetCountdown > 0)) && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, gap: 1.5 }}>
                {currentMeeting ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', color: '#C53030', fontWeight: 700 }}>
                      <Typography variant="body2" sx={{ mr: 0.5, animation: 'sttIconBlink 1000ms ease-in-out infinite' }}>
                        Trwa spotkanie
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ height: 28 }}
                      onClick={endCurrentTranscription}
                    >
                      Zakończ
                    </Button>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', color: '#C53030', fontWeight: 700 }}>
                      <Typography variant="body2" sx={{ mr: 0.5 }}>Spotkanie za:</Typography>
                      <Box
                        component="span"
                        sx={{
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          fontSize: '0.875rem',
                          display: 'inline-block',
                          width: '5ch',
                          textAlign: 'right'
                        }}
                      >
                        {`${String(Math.floor(meetCountdown/60)).padStart(2,'0')}:${String(meetCountdown%60).padStart(2,'0')}`}
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ height: 28 }}
                      onClick={openTranscriptionForNext}
                    >
                      Uruchom transkrypcję
                    </Button>
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* Pusta środkowa kolumna dla równowagi siatki */}
          <Box />

          {/* Prawa kolumna – dwie ikony + avatar */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifySelf: 'end', gap: 0 }}>
            {/* Akcje */}
            {isAuthenticated && !isLoading ? (
              <>
                {/* Avatary zalogowanych użytkowników – stack RTL, pierwszy 24px od separatora */}
                <Box sx={{ display: 'flex', alignItems: 'center', mr: '24px', flexDirection: 'row-reverse' }}>
                  {(() => {
                    const MAX = 4;
                    const list = (onlineUsers || [])
                      .filter(u => !!u && u.user_id !== user?.user_id)
                      .slice(0, MAX);
                    // render od prawej do lewej: pierwszy DOM element jest przy separatorze
                    return list.map((u, idx) => (
                      <Avatar
                        key={`topbar-online-${u.user_id}`}
                        src={(u as any).avatar_url || undefined}
                        sx={{ width: 28, height: 28, mr: idx === 0 ? 0 : '-3px', zIndex: 100 - idx }}
                      >
                        {((u as any).display_name || (u as any).email || '?').slice(0,1).toUpperCase()}
                      </Avatar>
                    ));
                  })()}
                </Box>
                {/* Separator przed grupą ikon */}
                <Box component="img" src="/assets/topbar/divider.svg" alt="divider" sx={{ display: 'block', mr: '24px' }} />
                
                {/* Grupa ikon 40x40: Gemma, zadania, dzwonek, telefon, chat, transkrypcja, avatar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875 }}>
                  {!isFieldWorkerViewer && (
                  <Tooltip
                    title={
                      gemmaAvailableTopbar
                        ? 'Zadania Gemmy (agent Spark) — dostępna; ikona się obraca'
                        : 'Zadania Gemmy — kolejka pracy agenta (nie Twoje „Moje zadania”)'
                    }
                  >
                    <span>
                      <IconButton
                        size="medium"
                        color="inherit"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGemmaTasks();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        title="Zadania Gemmy"
                        aria-label="Zadania Gemmy — kolejka agenta Spark"
                        sx={{
                          position: 'relative',
                          width: 40,
                          height: 40,
                          borderRadius: '4px',
                          padding: '0 !important',
                          minWidth: '40px !important',
                          minHeight: '40px !important',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&:hover': { bgcolor: 'action.hover' },
                          '& > *:not(.gemma-queue-badge)': {
                            pointerEvents: 'none !important',
                          },
                        }}
                      >
                        <GemmaAvatar size={20} spin={gemmaAvailableTopbar} />
                        {gemmaQueueActiveCount > 0 && (
                          <Box className="gemma-queue-badge" sx={{ pointerEvents: 'none' }}>
                            {renderBadge(gemmaQueueActiveCount)}
                          </Box>
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  )}
                  <IconButton 
                    size="medium" 
                    color="inherit" 
                    onClick={(e) => { e.stopPropagation(); toggleTasks(); fetchTasksCount(); }} 
                    onMouseDown={(e) => { e.stopPropagation(); }}
                    title="Zadania" 
                    sx={{ 
                      position: 'relative', 
                      width: 40, 
                      height: 40, 
                      borderRadius: '4px',
                      padding: '0 !important',
                      minWidth: '40px !important',
                      minHeight: '40px !important',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& > *:not(.MuiBadge-root)': {
                        pointerEvents: 'none !important',
                      },
                    }}
                  >
                    <TasksSvg width={20} height={20} />
                    {tasksCount > 0 && renderBadge(tasksCount)}
                  </IconButton>
                  <IconButton 
                    size="medium" 
                    color="inherit" 
                    onClick={(e) => { e.stopPropagation(); toggleNotif(); }} 
                    onMouseDown={(e) => { e.stopPropagation(); }}
                    title="Powiadomienia" 
                    sx={{ 
                      position: 'relative', 
                      width: 40, 
                      height: 40, 
                      borderRadius: '4px',
                      padding: '0 !important',
                      minWidth: '40px !important',
                      minHeight: '40px !important',
                      cursor: 'pointer',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: 'url("/assets/topbar/bell.svg")',
                        backgroundSize: '20px 20px',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        pointerEvents: 'none',
                      },
                      '& > *': {
                        pointerEvents: 'none !important',
                      },
                    }}
                  >
                    <Box sx={{ display: 'none' }} />
                    {unread > 0 && renderBadge(unread, 1.02, 10)}
                  </IconButton>
                  <Box sx={{ position: 'relative' }}>
                    <IconButton 
                      size="medium" 
                      color="inherit" 
                      onClick={(e) => { e.stopPropagation(); handleOpenCallMenu(e); }} 
                      onMouseDown={(e) => { e.stopPropagation(); }}
                      title="Połączenia" 
                      sx={{ 
                        position: 'relative',
                        width: 40, 
                        height: 40, 
                        borderRadius: '4px',
                        padding: '0 !important',
                        minWidth: '40px !important',
                        minHeight: '40px !important',
                        cursor: 'pointer',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundImage: 'url("/assets/topbar/phone.svg")',
                          backgroundSize: '20px 20px',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                          pointerEvents: 'none',
                        },
                        '& > *': {
                          pointerEvents: 'none !important',
                        },
                      }}
                    >
                      <Box sx={{ display: 'none' }} />
                    </IconButton>
                    {missedCalls > 0 && renderBadge(missedCalls)}
                  </Box>
                  <Box sx={{ position: 'relative' }}>
                    <IconButton 
                      size="medium" 
                      color="inherit" 
                      onClick={(e) => { e.stopPropagation(); toggleChat(); }} 
                      onMouseDown={(e) => { e.stopPropagation(); }}
                      title="Chat" 
                      sx={{ 
                        position: 'relative',
                        width: 40, 
                        height: 40, 
                        borderRadius: '4px',
                        padding: '0 !important',
                        minWidth: '40px !important',
                        minHeight: '40px !important',
                        cursor: 'pointer',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundImage: 'url("/assets/topbar/chat.svg")',
                          backgroundSize: '20px 20px',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                          pointerEvents: 'none',
                        },
                        '& > *': {
                          pointerEvents: 'none !important',
                        },
                      }}
                    >
                      <Box sx={{ display: 'none' }} />
                    </IconButton>
                    {unreadChat > 0 && renderBadge(unreadChat)}
                  </Box>
                  {(() => {
                    const remote = !sttRecording && remoteSttSessions.length > 0 ? remoteSttSessions[0] : null;
                    const href = remote
                      ? `/app/notes/transcription?meeting=${encodeURIComponent(remote.meetingGoogleId)}`
                      : nextMeeting
                        ? `/app/notes/transcription?meeting=${encodeURIComponent(nextMeeting.id)}&autostart=1`
                        : currentMeeting
                          ? `/app/notes/transcription?meeting=${encodeURIComponent(currentMeeting.id)}&autostart=1`
                          : '/app/notes/transcription';
                    const title = remote
                      ? `Trwa transkrypcja spotkania${remote.meetingTitle ? `: ${remote.meetingTitle}` : ''}${remote.startedBy?.displayName ? ` (prowadzi: ${remote.startedBy.displayName})` : ''}`
                      : 'Transkrypcja';
                    const showIcon = !!(nextMeeting || currentMeeting || sttRecording || remote);
                    const isRemoteBlink = !!remote && !sttRecording;
                    if (!showIcon) return null;
                    return (
                      <IconButton
                      size="medium"
                      color="inherit"
                      title={title}
                      component="a"
                      href={href}
                      target="_blank"
                      rel="noopener"
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                      }}
                    >
                      <Box
                        aria-label="transkrypcja"
                        sx={{
                          width: 20,
                          height: 20,
                          WebkitMaskImage: 'url("/assets/icons/voice_chat_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg")',
                          WebkitMaskRepeat: 'no-repeat',
                          WebkitMaskPosition: 'center',
                          WebkitMaskSize: 'contain',
                          maskImage: 'url("/assets/icons/voice_chat_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg")',
                          maskRepeat: 'no-repeat',
                          maskPosition: 'center',
                          maskSize: 'contain',
                          backgroundColor: (sttRecording || isRemoteBlink) ? '#C53030' : '#2D2D32',
                          ...((sttRecording || isRemoteBlink) ? {
                            animation: 'sttIconBlink 1000ms ease-in-out infinite',
                            '@keyframes sttIconBlink': {
                              '0%': { opacity: 1 },
                              '50%': { opacity: 0.35 },
                              '100%': { opacity: 1 }
                            }
                          } : {} )
                        }}
                      />
                    </IconButton>
                    );
                  })()}
                  <Box sx={{ '& .MuiIconButton-root': { ml: 0 } }}>
                    <UserProfile />
                  </Box>
                </Box>
              </>
            ) : (
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleLoginClick}
              >
                Zaloguj się
              </Button>
            )}
          </Box>
          {/* Portale/menu i overlay poza układem flex, aby nie psuły odstępów */}
          {isAuthenticated && !isLoading && (
                <>
              {(() => {
                const active = panelOrder.filter(k => (k==='notif'&&notifOpen)||(k==='chat'&&chatOpen)||(k==='tasks'&&tasksOpen)||(k==='ai'&&aiOpen)||(k==='gemmaTasks'&&gemmaTasksOpen));
                const zi = (k: 'notif'|'chat'|'tasks'|'ai'|'gemmaTasks') => {
                  const i = active.indexOf(k);
                  // Bardzo wysoki zIndex jak w oknie kamer, aby mieć pewność, że panel jest nad overlayem
                  return 2147483000 + (i >= 0 ? i : 0);
                };
                return (
                  <>
                    <NotificationDrawer open={notifOpen} onClose={() => { setOverlayReady(false); setNotifOpen(false); setPanelOrder(prev => prev.filter(k => k !== 'notif')); }} onUnreadChange={handleUnreadChange} zIndex={zi('notif')} onEntered={()=>setOverlayReady(true)} />
                    <TasksDrawer open={tasksOpen} onClose={() => { setOverlayReady(false); setTasksOpen(false); setPanelOrder(prev => prev.filter(k => k !== 'tasks')); }} zIndex={zi('tasks')} onEntered={()=>setOverlayReady(true)} />
                    {!isFieldWorkerViewer && (
                    <GemmaTasksDrawer
                      open={gemmaTasksOpen}
                      onClose={() => { setOverlayReady(false); setGemmaTasksOpen(false); setPanelOrder(prev => prev.filter(k => k !== 'gemmaTasks')); }}
                      zIndex={zi('gemmaTasks')}
                      onEntered={() => setOverlayReady(true)}
                    />
                    )}
                    {!isFieldWorkerViewer && (
                    <AIDialog open={aiOpen} onClose={() => { setOverlayReady(false); setAiOpen(false); setPanelOrder(prev => prev.filter(k => k !== 'ai')); }} zIndex={zi('ai')} onEntered={()=>setOverlayReady(true)} />
                    )}
                  </>
                );
              })()}
              <Menu
                anchorEl={callMenuAnchor}
                open={Boolean(callMenuAnchor)}
                onClose={handleCloseCallMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {missedList.length > 0 && (
                  <>
                    <MenuItem disabled>
                      <ListItemText primary="Nieodebrane" />
                    </MenuItem>
                    {missedList.map((m) => (
                      <MenuItem key={`missed-${m.userId}`} onClick={() => handleCallbackMissed(m.userId)}>
                        <ListItemIcon>
                          <Avatar src={m.avatarUrl || undefined} sx={{ width: 24, height: 24 }}>
                            {(m.displayName || '?').slice(0,1).toUpperCase()}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText primary={m.displayName || m.userId} secondary={new Date(m.at).toLocaleTimeString()} />
                      </MenuItem>
                    ))}
                    <MenuItem disabled>
                      <ListItemText primary="—" />
                    </MenuItem>
                  </>
                )}
                <MenuItem onClick={handleOpenConference}>
                  <ListItemIcon>
                    <GroupsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Telekonferencja…" />
                </MenuItem>
                <MenuItem disabled>
                  <ListItemText primary="Użytkownicy online" />
                </MenuItem>
                {onlineUsers.filter(u => u.user_id !== user?.user_id).length === 0 ? (
                  <MenuItem disabled>
                    <ListItemText primary="Brak użytkowników online" />
                  </MenuItem>
                ) : (
                  onlineUsers
                    .filter(u => u.user_id !== user?.user_id)
                    .map((u) => (
                      <MenuItem key={`call-${u.user_id}`} onClick={() => handleStartCall(u.user_id)}>
                        <ListItemIcon>
                          <Avatar src={(u as any).avatar_url || undefined} sx={{ width: 24, height: 24 }}>
                            {((u as any).display_name || (u as any).email || u.user_id || '?').slice(0,1).toUpperCase()}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText primary={(u as any).display_name || (u as any).email || u.user_id} />
                      </MenuItem>
                    ))
                )}
              </Menu>
              <IncomingCallToast
                  open={incomingOpen}
                  fromDisplayName={incomingFrom || undefined}
                  fromAvatarUrl={incomingAvatar || undefined}
                  leftSeconds={incomingLeftSec}
                  callKey={incomingCallKey}
                  isConference={incomingIsConference}
                  participantsCount={incomingParticipantsCount}
                  onAccept={handleAcceptIncoming}
                  onDecline={handleDeclineIncoming}
                />
              <ConferenceCallDialog
                open={conferenceOpen}
                onClose={() => { if (!conferenceStarting) setConferenceOpen(false); }}
                onStart={handleStartConference}
                currentUserId={user?.user_id || null}
                onlineUserIds={onlineUsers.map((u) => u.user_id)}
                starting={conferenceStarting}
              />
            </>
          )}
          {/* Overlay i dialogi niezależnie od kolumny */}
          <GlobalSearchOverlay
            anchorEl={anchorEl}
            open={overlayItems.length > 0}
            title="Podpowiedzi"
            items={overlayItems}
            emptyText="Brak propozycji"
            selectedIndex={selectedIdx}
            onHoverItem={(i)=>setSelectedIdx(i)}
            onClose={()=>{ setOverlayItems([]); }}
          />
          <ScreenShareConsent
            open={consentOpen}
            onAccept={() => { handleConsentAccept(); }}
            onDecline={() => { if (requestFromUserId) sendScreenshareConsent(requestFromUserId, false); setConsentOpen(false); setRequestFromUserId(null); }}
          />
        </Toolbar>
      </AppBar>
      {/* Globalny overlay wyłączony – każdy panel ma własny Backdrop MUI */}
      {/* Dymek nowych wiadomości w prawym górnym rogu */}
      {(() => {
        let runningOffset = 0;
        const GAP = 12; // odstęp między dymkami
        const TEXT_H = 88; // musi odpowiadać HEIGHT_TEXT z ChatMessageToast
        const IMAGE_H = 160; // musi odpowiadać HEIGHT_IMAGE z ChatMessageToast
        return toasts.map((t) => {
          const offset = runningOffset;
          const h = t.imageUrl ? IMAGE_H : TEXT_H;
          runningOffset += h + GAP;
          return (
            <ChatMessageToast
              key={t.id}
              open={true}
              roomTitle={t.room || undefined}
              fromDisplayName={t.from || undefined}
              fromAvatarUrl={t.avatar || undefined}
              message={t.text}
              imageUrl={t.imageUrl || undefined}
              createdAt={t.createdAt || undefined}
              offset={offset}
              onClick={async () => {
                try {
                  setChatOpen(true);
                  if (t.convType === 'group' && t.convId) {
                    // jeśli znamy id rozmowy grupowej – ustaw aktywną
                    setActiveChatSpace({ id: t.convId, type: 'group', name: t.room || 'Pokój' } as any);
                  } else if (t.convType === 'direct' && t.fromUserId) {
                    // utwórz/otwórz DM z nadawcą
                    try {
                      const conv = await getOrCreateDirect(t.fromUserId);
                      setActiveChatSpace(conv as any);
                    } catch {}
                  }
                } finally {
                  closeToast(t.id);
                }
              }}
              onClose={() => closeToast(t.id)}
            />
          );
        });
      })()}
      {/* Panel chatu z prawej */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={chatOpen}
        onClose={() => { setOverlayReady(false); setChatOpen(false); setActiveChatSpace(null); setActiveChatUser(null); setPanelOrder(prev => prev.filter(k => k !== 'chat')); }}
        sx={{
          zIndex: (() => {
            const active = panelOrder.filter(k => (k==='notif'&&notifOpen)||(k==='chat'&&chatOpen)||(k==='tasks'&&tasksOpen)||(k==='ai'&&aiOpen)||(k==='gemmaTasks'&&gemmaTasksOpen));
            const i = active.indexOf('chat');
            return i >= 0 ? (2147483000 + i) : 2147483000;
          })()
        }}
        ModalProps={{ disableEnforceFocus: true, disableAutoFocus: true, keepMounted: true, BackdropProps: { sx: { backgroundColor: 'rgba(0,0,0,0.35)' } } }}
        SlideProps={{ onEntered: () => { try { setOverlayReady(true); } catch {} } } as any}
        PaperProps={{ sx: {
          width: 420, maxWidth: '100vw', minWidth: 320, top: 0, height: '100vh', bottom: 0, borderRadius: 0, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          right: 0,
          transform: (() => {
            const active = panelOrder.filter(k => (k==='notif'&&notifOpen)||(k==='chat'&&chatOpen)||(k==='tasks'&&tasksOpen)||(k==='ai'&&aiOpen)||(k==='gemmaTasks'&&gemmaTasksOpen));
            const i = active.indexOf('chat');
            const offset = (active.length - 1 - i) * 420;
            return i >= 0 ? `translateX(-${offset}px)` : 'none';
          })(),
          pointerEvents: 'auto'
        },
        onTransitionEnd: (e: React.TransitionEvent) => { try { if ((e as any).propertyName === 'transform') setOverlayReady(true); } catch {} }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <ChannelDialog
            open={channelDialogOpen}
            onClose={() => { setChannelDialogOpen(false); setEditingChannelId(null); setEditingChannelTitle(''); }}
            conversationId={editingChannelId}
            initialTitle={editingChannelTitle}
            onSuccess={() => {
              setChatLoading(true);
              listSpaces().then(spaces => { setChatSpaces(spaces); setChatLoading(false); }).catch(() => { setChatLoading(false); });
            }}
          />
          {/* Nagłówek w stylu „Moje zadania” / „Rejestr zdarzeń” */}
          <Box sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff', borderBottom: '1px solid #eee' }}>
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="img" src="/assets/topbar/chat.svg" alt="chat" sx={{ width: 20, height: 20 }} />
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>Chat</Typography>
              </Box>
              <IconButton onClick={()=>{ setChatOpen(false); setActiveChatSpace(null); setActiveChatUser(null); setPanelOrder(prev => prev.filter(k => k !== 'chat')); }} aria-label="Zamknij">
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              {user?.role === 'admin' && (
              <Button
                variant="contained"
                onClick={() => {
                  setEditingChannelId(null);
                  setEditingChannelTitle('');
                  setChannelDialogOpen(true);
                }}
                sx={{
                  width: 116,
                  height: 40,
                  borderRadius: '4px',
                  textTransform: 'none',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  wordBreak: 'keep-all',
                  backgroundColor: '#4040DB',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#1F1FB0' },
                  '&:active': { backgroundColor: '#1A1A98' }
                }}
              >
                Dodaj
              </Button>
              )}
              <Paper
                elevation={0}
                sx={{
                  px: 0,
                  py: 0,
                  ml: 1,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #ededed',
                  height: 36,
                  width: 260,
                  '&:hover': { borderColor: '#B9B9B9' }
                }}
              >
                <Box component="img" src="/assets/topbar/search.svg" alt="search" sx={{ ml: 1.5 }} />
                <InputBase
                  placeholder="Szukaj"
                  value={chatFilter}
                  onChange={(e)=>setChatFilter(e.target.value)}
                  sx={{ pl: 1.5, pr: 1.5, flex: 1, fontSize: 14 }}
                  inputProps={{ 'aria-label': 'szukaj w chacie', 'data-role': 'chat-filter' }}
                />
              </Paper>
            </Box>
          </Box>
          {/* DYNAMICZNY pasek w rozmowie – tylko powrót i nazwa pokoju */}
          {activeChatSpace && (
            <>
              <Box sx={{ px: 2, py: 1, mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="img"
                  src="/assets/icons/arrow-right.svg"
                  alt="Wróć"
                  sx={{ width: 10, height: 10, cursor: 'pointer', transform: 'rotate(180deg)', transition: 'transform 120ms ease' }}
                  onClick={()=>{ setActiveChatSpace(null); setActiveChatUser(null); }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', color: '#020242', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {activeChatSpace?.name || ''}
                </Typography>
              </Box>
              <Divider />
            </>
          )}
          {activeChatSpace ? (
            <>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <InternalChatPanel activeConversation={activeChatSpace as any} showHeader={false} />
              </Box>
            </>
          ) : (
            <>
              {isFieldWorkerViewer && (
                <Box sx={{ px: 2, py: 1, mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', color: '#020242', mb: 0.5 }}
                  >
                    Kanał
                  </Typography>
                  {fieldWorkersGroupChannel ? (() => {
                    const s = fieldWorkersGroupChannel as any;
                    const unread = Number(s.unreadCount || roomUnreads[s.id] || 0);
                    const name = String(s.name || CHAT_FIELD_WORKERS_GROUP_NAME);
                    return (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.2, px: 1.5, py: 1, borderRadius: 1, cursor: 'pointer', '&:hover': { backgroundColor: '#F5F5FF' } }}
                        onClick={async () => {
                          setActiveChatSpace(s as any);
                          try { await markReadConv(s.id); } catch {}
                          setChatSpaces((prev: any) => prev.map((c: any) => (c.id === s.id ? { ...c, unreadCount: 0 } : c)));
                          setRoomUnreads((prev) => {
                            const nm: Record<string, number> = { ...prev, [s.id]: 0 };
                            const total = Object.values(nm).reduce((a: number, b: number) => a + (b || 0), 0);
                            setUnreadChat(total);
                            return nm;
                          });
                        }}
                      >
                        <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#2D2D32' }}>#</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }}>{name}</Typography>
                        </Box>
                        {unread > 0 && (
                          <Box sx={{ backgroundColor: 'error.main', color: 'common.white', borderRadius: '12px', px: 0.75, minWidth: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, lineHeight: 1 }}>{unread}</Box>
                        )}
                      </Box>
                    );
                  })() : !chatLoading ? (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>Kanał będzie widoczny po odświeżeniu listy</Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Ładowanie…</Typography>
                  )}
                </Box>
              )}
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {!isFieldWorkerViewer && (
                <Box
                  sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.2, px: 1.5, py: 0.9, borderRadius: 1, cursor: 'pointer', mt: 2, '&:hover': { backgroundColor: '#F5F5FF' } }}
                  onClick={openAiDrawer}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GemmaAvatar size={20} />
                    <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }}>Gemma</Typography>
                  </Box>
                  {aiHasNew && !aiBusy && (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', marginLeft: 'auto' }} />
                  )}
                </Box>
                )}
                {!isFieldWorkerViewer && (
                <>
                <Box sx={{ px: 2, py: 1, mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    component="img"
                    src="/assets/icons/arrow-right.svg"
                    alt="toggle"
                    sx={{ width: 10, height: 10, cursor: 'pointer', transform: roomsCollapsed ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'transform 120ms ease' }}
                    onClick={()=>setRoomsCollapsed(v=>!v)}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', color: '#020242', cursor: 'pointer' }}
                    onClick={()=>setRoomsCollapsed(v=>!v)}
                  >
                    Kanały
                  </Typography>
                </Box>
                {chatLoading ? (
                  <Typography variant="body2" color="text.secondary">Ładowanie…</Typography>
                ) : roomsCollapsed ? null : (chatSpaces && chatSpaces.filter((x:any)=> x?.type==='group').length === 0) ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Brak rozmów.</Typography>
                  </Box>
                ) : (() => {
                  const merged = sortRooms((chatSpaces as any)
                    .filter((x:any)=> x?.type==='group')
                    .filter((s:any) => !isFieldWorkersGroupChannelName((s as any)?.name))
                    .filter((s:any)=> ((s?.name || '').toLowerCase()).includes((chatFilter||'').trim().toLowerCase())));
                  return merged.map((s:any) => {
                    const unread = Number((s as any).unreadCount || roomUnreads[s.id] || 0);
                    const name = String((s as any).name || '');
                    const lower = name.trim().toLowerCase();
                    const isVirtual = String(s.id || '').startsWith('__virt:');
                    const isGeneral = lower === 'wiadomości ogólne' || lower === 'wiadomosci ogólne' || lower === 'wiadomosci ogolne' || lower === 'wiadomości ogolne';
 // bez strzałki dla „Wiadomości ogólne” i wirtualnych
                    const onOpen = async () => {
                      if (isVirtual) return; // brak rozmowy – tylko wizualnie
                      setActiveChatSpace(s as any);
                      try { await markReadConv(s.id); } catch {}
                      setChatSpaces((prev:any) => prev.map((c:any) => c.id===s.id ? ({ ...c, unreadCount: 0 }) : c));
                      setRoomUnreads(prev => { 
                        const nm: Record<string, number> = { ...prev, [s.id]: 0 } as Record<string, number>;
                        const total = Object.values(nm as Record<string, number>).reduce((a: number, b: number) => a + (b || 0), 0);
                        setUnreadChat(total);
                        return nm; 
                      });
                    };
                    return (
                      <Box key={s.id || name}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.2,
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#F5F5FF' }
                        }}
                        onClick={onOpen}
                      >
                        <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#2D2D32' }}>
                          #
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }}>
                            {name}
                          </Typography>
                        </Box>
                        {unread > 0 && (
                          <Box sx={{ backgroundColor: 'error.main', color: 'common.white', borderRadius: '12px', px: 0.75, minWidth: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, lineHeight: 1 }}>
                            {unread}
                          </Box>
                        )}
                        {!isVirtual && (
                          <IconButton
                            size="small"
                            sx={{ ml: 0.5, opacity: 0.6, '&:hover': { opacity: 1 } }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setEditingChannelId(s.id);
                              setEditingChannelTitle(name);
                              setChannelDialogOpen(true);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    );
                  });
                })()}
                </>
                )}

                {/* Sekcja DM – prywatne (bez ekipy terenowej; ekipa jest w sekcji niżej) — u pracownika terenowego: z każdym użytkownikiem */}
                <Box sx={{ mt: 1.5 }} />
                <Box sx={{ px: 2, py: 1, mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    component="img"
                    src="/assets/icons/arrow-right.svg"
                    alt="toggle"
                    sx={{ width: 10, height: 10, cursor: 'pointer', transform: dmExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 120ms ease' }}
                    onClick={()=>setDmExpanded(v=>!v)}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', color: '#020242', cursor: 'pointer' }}
                    onClick={()=>setDmExpanded(v=>!v)}
                  >
                    Prywatne rozmowy
                  </Typography>
                </Box>
                {dmExpanded && (
                  <Box>
                    {allUsers.filter(
                      (u) => u.user_id !== user?.user_id && !isInternalChatGemmaSystemUserId(u.user_id) && (isFieldWorkerViewer || u.role !== 'field_worker'),
                    ).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Brak użytkowników</Typography>
                    ) : (
                      allUsers
                        .slice()
                        .filter((u) => !isInternalChatGemmaSystemUserId(u.user_id) && (isFieldWorkerViewer || u.role !== 'field_worker'))
                        .sort((a,b)=>{
                          const aOnline = onlineUsers.some(o=>o.user_id===a.user_id);
                          const bOnline = onlineUsers.some(o=>o.user_id===b.user_id);
                          if (aOnline && !bOnline) return -1;
                          if (!aOnline && bOnline) return 1;
                          const an = (a.display_name || a.email || '').toLowerCase();
                          const bn = (b.display_name || b.email || '').toLowerCase();
                          return an.localeCompare(bn);
                        })
                        .filter((u) => u.user_id !== user?.user_id)
                        .filter((u)=> {
                          const q = (chatFilter||'').trim().toLowerCase();
                          if (!q) return true;
                          const name = ((u.display_name || u.email || u.user_id || '') as string).toLowerCase();
                          return name.includes(q);
                        })
                        .map(u => {
                          const conv = (chatSpaces as any).find((c:any)=> c.type==='direct' && c.otherUserId === u.user_id);
                          const unread = conv ? Number(conv.unreadCount || 0) : 0;
                          return (
                    <Box key={`dm-${u.user_id}`} sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.2, px: 1.5, py: 0.9, borderRadius: 1, cursor: 'pointer', '&:hover': { backgroundColor: '#F5F5FF' } }} onClick={async ()=>{
                              try {
                                const conv = await getOrCreateDirect(u.user_id);
                                setActiveChatSpace(conv as any);
                                try { await markReadConv(conv.id); } catch {}
                                setChatSpaces(prev => prev.map(c => c.id===conv.id ? ({ ...c, unreadCount: 0 }) as any : c));
                              } catch {}
                            }}>
                              <Avatar src={u.avatar_url || undefined} sx={{ width: 20, height: 20 }}>
                                {(u.display_name || u.email || '?').slice(0,1).toUpperCase()}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }}>
                                {(u.display_name || u.email || u.user_id)}
                                {(() => { try { return (localStorage.getItem(`dnd:${u.user_id}`)||'0')==='1'; } catch { return false; } })() && (
                                  <Typography component="span" variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}> (zajęty)</Typography>
                                )}
                              </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: 'auto' }}>
                              {unread > 0 && (
                                <Box sx={{ backgroundColor: 'error.main', color: 'common.white', borderRadius: '12px', px: 0.75, minWidth: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, lineHeight: 1 }}>{unread}</Box>
                              )}
                              {(() => {
                                const ou = onlineUsers.find(o=>o.user_id===u.user_id);
                                if (!ou) return null; // tylko dla online
                                let isDnd = !!(ou as any).dnd;
                                try {
                                  // fallback, gdy backend jeszcze nie zwraca dnd
                                  const ls = localStorage.getItem(`dnd:${u.user_id}`) || (u.user_id === user?.user_id ? localStorage.getItem('chat:dnd') : '0');
                                  if (ls === '1') isDnd = true;
                                } catch {}
                                return (
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isDnd ? 'error.main' : 'success.main' }} />
                                );
                              })()}
                            </Box>
                          </Box>
                          );
                        })
                    )}
                  </Box>
                )}

                {/* Pracownicy terenowi: kanał # Wszyscy + prywatne tylko z ekipą terenową (role field_worker) — ukryte, gdy zalogowany jest pracownik terenowy */}
                {!isFieldWorkerViewer && (
                <>
                <Box sx={{ mt: 1.5 }} />
                <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    component="img"
                    src="/assets/icons/arrow-right.svg"
                    alt="toggle"
                    sx={{ width: 10, height: 10, cursor: 'pointer', transform: fieldWorkersExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 120ms ease' }}
                    onClick={() => setFieldWorkersExpanded((v) => !v)}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', color: '#020242', cursor: 'pointer' }}
                    onClick={() => setFieldWorkersExpanded((v) => !v)}
                  >
                    Pracownicy terenowi
                  </Typography>
                </Box>
                {fieldWorkersExpanded && (
                  <Box>
                    {fieldWorkersGroupChannel ? (() => {
                      const s = fieldWorkersGroupChannel as any;
                      const unread = Number(s.unreadCount || roomUnreads[s.id] || 0);
                      const name = String(s.name || CHAT_FIELD_WORKERS_GROUP_NAME);
                      return (
                        <Box
                          key="field-workers-group"
                          sx={{ display: 'flex', alignItems: 'center', gap: 1.2, px: 1.5, py: 1, borderRadius: 1, cursor: 'pointer', '&:hover': { backgroundColor: '#F5F5FF' } }}
                          onClick={async () => {
                            setActiveChatSpace(s as any);
                            try { await markReadConv(s.id); } catch {}
                            setChatSpaces((prev: any) => prev.map((c: any) => c.id === s.id ? { ...c, unreadCount: 0 } : c));
                            setRoomUnreads((prev) => {
                              const nm: Record<string, number> = { ...prev, [s.id]: 0 };
                              const total = Object.values(nm).reduce((a: number, b: number) => a + (b || 0), 0);
                              setUnreadChat(total);
                              return nm;
                            });
                          }}
                        >
                          <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#2D2D32' }}>#</Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }}>{name}</Typography>
                          </Box>
                          {unread > 0 && (
                            <Box sx={{ backgroundColor: 'error.main', color: 'common.white', borderRadius: '12px', px: 0.75, minWidth: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, lineHeight: 1 }}>{unread}</Box>
                          )}
                        </Box>
                      );
                    })() : !chatLoading ? (
                      <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 0.5 }}>Kanał będzie widoczny po odświeżeniu listy</Typography>
                    ) : null}
                    {allUsers.filter(
                      (u) => u.user_id !== user?.user_id && u.role === 'field_worker' && !isInternalChatGemmaSystemUserId(u.user_id)
                    ).length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ px: 1.5 }}>Brak pracowników terenowych do rozmów prywatnych</Typography>
                    ) : (
                      allUsers
                        .slice()
                        .filter((u) => u.user_id !== user?.user_id && u.role === 'field_worker' && !isInternalChatGemmaSystemUserId(u.user_id))
                        .sort((a, b) => {
                          const aOnline = onlineUsers.some((o) => o.user_id === a.user_id);
                          const bOnline = onlineUsers.some((o) => o.user_id === b.user_id);
                          if (aOnline && !bOnline) return -1;
                          if (!aOnline && bOnline) return 1;
                          const an = (a.display_name || a.email || '').toLowerCase();
                          const bn = (b.display_name || b.email || '').toLowerCase();
                          return an.localeCompare(bn);
                        })
                        .filter((u) => {
                          const q = (chatFilter || '').trim().toLowerCase();
                          if (!q) return true;
                          const name = ((u.display_name || u.email || u.user_id || '') as string).toLowerCase();
                          return name.includes(q);
                        })
                        .map((u) => {
                          const conv = (chatSpaces as any).find((c: any) => c.type === 'direct' && c.otherUserId === u.user_id);
                          const unread = conv ? Number(conv.unreadCount || 0) : 0;
                          return (
                            <Box
                              key={`dm-fw-${u.user_id}`}
                              sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.2, px: 1.5, py: 0.9, borderRadius: 1, cursor: 'pointer', '&:hover': { backgroundColor: '#F5F5FF' } }}
                              onClick={async () => {
                                try {
                                  const c = await getOrCreateDirect(u.user_id);
                                  setActiveChatSpace(c as any);
                                  try { await markReadConv(c.id); } catch {}
                                  setChatSpaces((prev) => prev.map((row) => (row.id === c.id ? ({ ...row, unreadCount: 0 } as any) : row)));
                                } catch {}
                              }}
                            >
                              <Avatar src={u.avatar_url || undefined} sx={{ width: 20, height: 20 }}>
                                {(u.display_name || u.email || '?').slice(0, 1).toUpperCase()}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }}>
                                {u.display_name || u.email || u.user_id}
                                {(() => { try { return (localStorage.getItem(`dnd:${u.user_id}`) || '0') === '1'; } catch { return false; } })() && (
                                  <Typography component="span" variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}> (zajęty)</Typography>
                                )}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: 'auto' }}>
                                {unread > 0 && (
                                  <Box sx={{ backgroundColor: 'error.main', color: 'common.white', borderRadius: '12px', px: 0.75, minWidth: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, lineHeight: 1 }}>{unread}</Box>
                                )}
                                {(() => {
                                  const ou = onlineUsers.find((o) => o.user_id === u.user_id);
                                  if (!ou) return null;
                                  let isDnd = !!(ou as any).dnd;
                                  try {
                                    const ls = localStorage.getItem(`dnd:${u.user_id}`) || (u.user_id === user?.user_id ? localStorage.getItem('chat:dnd') : '0');
                                    if (ls === '1') isDnd = true;
                                  } catch {}
                                  return <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isDnd ? 'error.main' : 'success.main' }} />;
                                })()}
                              </Box>
                            </Box>
                          );
                        })
                    )}
                  </Box>
                )}
                </>
                )}
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default Header;
